import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Product } from '@/schemas/products';

interface ProductsState {
  byId: Record<string, Product>;
  allIds: string[];
  filters: {
    search?: string;
    category?: string;
    status?: string;
  };
  isLoading: boolean;
  error: string | null;
}

const initialState: ProductsState = {
  byId: {},
  allIds: [],
  filters: {},
  isLoading: false,
  error: null,
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setProducts: (state, action: PayloadAction<Product[]>) => {
      state.byId = {};
      state.allIds = [];
      action.payload.forEach((product) => {
        state.byId[product.id] = product;
        state.allIds.push(product.id);
      });
    },
    addProduct: (state, action: PayloadAction<Product>) => {
      const product = action.payload;
      state.byId[product.id] = product;
      if (!state.allIds.includes(product.id)) {
        state.allIds.push(product.id);
      }
    },
    updateProduct: (state, action: PayloadAction<Product>) => {
      const product = action.payload;
      state.byId[product.id] = product;
    },
    deleteProduct: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    setFilters: (state, action: PayloadAction<Partial<ProductsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setProducts,
  addProduct,
  updateProduct,
  deleteProduct,
  setFilters,
  setLoading,
  setError,
} = productsSlice.actions;

export default productsSlice.reducer;
